import { ButtonProps } from '@/components/Buttons';
import { RoleplayCard, RoleplayCardProps } from '@/components';
import { BUTTON_LABELS } from '@/constants';
import { IPersona, ISimulationScore, ISimulation } from '@/types';
import { mapSimulationToRoleplayCard } from '@/utils';
import { MouseEvent, useMemo } from 'react';

interface Props {
  certificationPractice: ISimulation;
  getRedFailBorder: (simulation: ISimulation, scores: ISimulationScore[]) => string;
  isLoading: boolean;
  onClick: (simulation: ISimulation) => (event?: MouseEvent<HTMLButtonElement>) => void;
  onShareWithManager: RoleplayCardProps['onShareWithManager'];
  onViewScoreCard: RoleplayCardProps['onViewScoreCard'];
  personas: IPersona[];
  scores: ISimulationScore[];
  userId?: string;
}

/**
 * CertificationSimulationsPractice is responsible for rendering a single roleplay
 * card that represents the practice simulation for a certification. It uses the
 * `mapSimulationToRoleplayCard` utility to transform the `certificationPractice`
 * simulation into the appropriate props for the `RoleplayCard` component, and it
 * also computes the action button configuration based on the simulation's status
 * and history.
 */
export const CertificationSimulationsPractice: React.FC<Props> = ({
  certificationPractice,
  getRedFailBorder,
  isLoading,
  onClick,
  onShareWithManager,
  onViewScoreCard,
  personas,
  scores,
  userId,
}) => {
  // ==============================
  // Memoized values
  // ==============================

  // Extract commonly used properties from the `certificationPractice`
  // simulation for easier access.
  const { description, history, status, title } = useMemo(
    () => certificationPractice || {},
    [certificationPractice],
  );

  /**
   * Maps the current `certificationPractice` simulation into `RoleplayCardProps`
   * using the `mapSimulationToRoleplayCard` utility function.
   */
  const roleplayCardProps = useMemo(() => {
    return mapSimulationToRoleplayCard({
      simulation: certificationPractice,
      personas,
      scores,
      onClick: onClick(certificationPractice),
    });
  }, [certificationPractice, personas, scores, onClick]);

  /**
   * Determines the action button props for the roleplay card based on the
   * simulation's status and history.
   */
  const roleplayCardAction = useMemo<ButtonProps>(() => {
    // Simplified constants for action button logic
    const { TRY_AGAIN, GET_STARTED } = BUTTON_LABELS;
    const hasHistory = history?.length !== 0;
    const isCompleted = status === 'completed';
    const isLocked = status === 'locked';
    const tryAgain = isCompleted || hasHistory;

    // Return the action button props based on the simulation status and history
    return {
      label: tryAgain ? TRY_AGAIN : GET_STARTED,
      onClick: onClick(certificationPractice),
      type: 'button',
      variant: hasHistory ? 'primary' : 'secondary',
      disabled: isLocked,
    };
  }, [certificationPractice, onClick, status, history]);

  // ==============================
  // Render
  // ==============================
  return (
    <div>
      <RoleplayCard
        {...roleplayCardProps}
        actions={[roleplayCardAction]}
        className={getRedFailBorder(certificationPractice, scores)}
        description={description}
        isLoading={isLoading}
        isNew={false}
        onShareWithManager={onShareWithManager}
        onViewScoreCard={onViewScoreCard}
        status={status}
        title={title}
        userId={userId}
      />
    </div>
  );
};
